import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import { seoConfig } from "../data/seoConfig";
import {
  getLocalizedKeywordTerms,
  getLocalizedPageByKey,
  localizedLocales,
  localizedPath,
  parseLocalizedPriorityPath,
} from "../data/localizedPriorityPages";
import { languageEditionAlternates } from "../data/siteNavigation";

function buildSchema(page, locale, path, keywords) {
  const language = localizedLocales[locale].lang;
  const canonical = `${seoConfig.baseUrl}${path}`;
  const englishUrl = `${seoConfig.baseUrl}${page.basePath === "/" ? "/" : page.basePath}`;
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": page.pageType === "article" ? "Article" : "WebPage",
    headline: page.title[locale],
    name: page.title[locale],
    description: page.description[locale],
    url: canonical,
    inLanguage: language,
    dateModified: page.modified,
    keywords,
    isPartOf: {
      "@type": "WebSite",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/`,
    },
    ...(!page.independentEdition ? { translationOfWork: {
      "@type": page.pageType === "article" ? "Article" : "WebPage",
      url: englishUrl,
      inLanguage: "en",
    } } : {}),
    author: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/about-techniahq`,
    },
    publisher: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/`,
      logo: {
        "@type": "ImageObject",
        url: `${seoConfig.baseUrl}/techniahqrobot-logo-20260715.png`,
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: localizedLocales[locale].labels.home,
        item: `${seoConfig.baseUrl}${localizedPath(locale, "/")}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: page.subject[locale],
        item: canonical,
      },
    ],
  };

  return [pageSchema, breadcrumbSchema];
}

function MissingLocalizedPage({ locale, path = "/404" }) {
  const config = localizedLocales[locale] || localizedLocales.fr;
  const copy = {
    fr: { title: "Page introuvable", description: "Cette page n’est pas disponible en français." },
    de: { title: "Seite nicht gefunden", description: "Diese Seite ist auf Deutsch nicht verfügbar." },
    ja: { title: "ページが見つかりません", description: "このページの日本語版はありません。" },
    "zh-cn": { title: "找不到页面", description: "此页面暂无中文版本。" },
  }[locale] || { title: "Page introuvable", description: "Cette page n’est pas disponible en français." };
  return (
    <>
      <SEO
        title={`${copy.title} TechniaHQRobot`}
        description={copy.description}
        path={path}
        noindex
        lang={config.lang}
      />
      <section lang={config.lang} className="site-shell py-20">
        <h1 className="text-4xl font-black">404 · {copy.title}</h1>
        <p className="mt-4 text-neutral-600">{copy.description}</p>
        <Link to={localizedPath(locale, "/")} className="btn-primary mt-6">
          {config.labels.home}
        </Link>
      </section>
    </>
  );
}

export default function LocalizedPriorityPage({ locale }) {
  const location = useLocation();
  const parsed = parseLocalizedPriorityPath(location.pathname);
  if (!parsed || parsed.locale !== locale) return <MissingLocalizedPage locale={locale} path={location.pathname} />;

  const page = { ...parsed.page, ...parsed.page.editorialByLocale?.[locale] };
  const config = localizedLocales[locale];
  const labels = { ...config.labels, ...page.labels };
  const subject = page.subject[locale];
  const keywords = getLocalizedKeywordTerms(page, locale);
  const currentPath = localizedPath(locale, page.basePath);
  const englishPath = page.basePath === "/" ? "/" : page.basePath;
  const related = (page.related || [])
    .map((key) => getLocalizedPageByKey(key))
    .filter(Boolean);

  return (
    <>
      <SEO
        title={page.title[locale]}
        description={page.description[locale]}
        path={currentPath}
        type={page.pageType === "article" ? "article" : "website"}
        modifiedTime={page.modified}
        section={subject}
        tags={keywords}
        lang={config.lang}
        alternates={page.basePath === "/" ? languageEditionAlternates : undefined}
        schema={buildSchema(page, locale, currentPath, keywords)}
      />

      <article className="pb-16" lang={config.lang}>
        <section className="border-b border-neutral-200 bg-gradient-to-b from-blue-50 via-white to-white">
          <div className="site-shell py-10 sm:py-14">
            <div className="max-w-5xl">
              <p className="eyebrow-chip">{labels.localEdition}</p>
              <h1 className="mt-5 text-4xl font-black tracking-tight text-neutral-950 sm:text-6xl">
                {page.title[locale]}
              </h1>
              <p className="mt-6 max-w-4xl text-lg leading-9 text-neutral-700">{page.lead[locale]}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a href={englishPath} hrefLang="en" className="btn-secondary">
                  {labels.englishOriginal}
                </a>
                <span className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-bold text-neutral-600">
                  {labels.updated} {page.modified}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="site-shell py-12">
          <div className={page.hideEvidence ? "grid gap-8" : "grid gap-8 lg:grid-cols-[1.15fr_.85fr]"}>
            <div className="article-panel">
              <p className="eyebrow-chip">{labels.overview}</p>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950">{subject}</h2>
              <p className="mt-5 text-base leading-8 text-neutral-700">{page.description[locale]}</p>
            </div>

            {!page.hideEvidence && <aside className="rounded-[2rem] border border-neutral-200 bg-neutral-950 p-6 text-white shadow-sm">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-300">{labels.evidence}</p>
              <p className="mt-5 text-base leading-8 text-neutral-300">{page.methodText || config.method[page.method]}</p>
              {page.sourceLinks?.length > 0 && (
                <div className="mt-6 grid gap-2">
                  {page.sourceLinks.map((source, index) => (
                    <a key={`${page.key}-source-${index}`} href={source.url} target="_blank" rel="noopener noreferrer" className="rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-sm font-bold text-blue-100 hover:border-blue-300/50">
                      {typeof source.label === "string" ? source.label : source.label?.[locale] || source.url}
                    </a>
                  ))}
                </div>
              )}
            </aside>}
          </div>
        </section>

        {page.facts?.length > 0 && <section className="border-y border-neutral-200 bg-neutral-50 py-12">
          <div className="site-shell">
            <p className="eyebrow-chip">{labels.verifiedFacts}</p>
            <div className="mt-6 overflow-hidden rounded-[2rem] border border-neutral-200 bg-white shadow-sm">
              <dl className="divide-y divide-neutral-200">
                {page.facts.map((fact, index) => (
                  <div key={`${page.key}-${index}`} className="grid gap-2 px-6 py-5 sm:grid-cols-[minmax(180px,.35fr)_1fr] sm:gap-6">
                    <dt className="font-black text-neutral-950">{fact.label[locale]}</dt>
                    <dd className="leading-7 text-neutral-700">{fact.value[locale]}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>}

        {page.sections?.length > 0 && (
          <section className="site-shell py-12">
            <div className="mx-auto grid max-w-4xl gap-5">
              {page.sections.map((section, index) => (
                <section key={`${page.key}-section-${index}`} className="article-panel h-full">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                    {String(index + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-4 text-2xl font-black tracking-tight text-neutral-950">
                    {section.heading[locale]}
                  </h2>
                  <p className="mt-5 text-base leading-8 text-neutral-700">
                    {section.body[locale]}
                  </p>
                </section>
              ))}
            </div>
          </section>
        )}

        {!page.hideRelated && <section className="border-y border-neutral-200 bg-neutral-50 py-12">
          <div className="site-shell">
            <p className="eyebrow-chip">{labels.related}</p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {related.map((item) => (
                <Link
                  key={item.key}
                  to={localizedPath(locale, item.basePath)}
                  hrefLang={config.lang}
                  className="soft-card soft-card-hover p-5"
                >
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700">{labels.read}</p>
                  <h3 className="mt-3 text-lg font-black leading-7 text-neutral-950">{item.subject[locale]}</h3>
                  <p className="mt-3 text-sm leading-7 text-neutral-600">{item.description[locale]}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>}

      </article>
    </>
  );
}
